import dayjs from 'dayjs'
import c from 'currency.js'
import round from './round'
import { ReceiptStatusHistory, TenderStatusHistoryEnum } from '../models'

export const date = (date: Date | null | string) => {
    return date ? dayjs(date).format('DD.MM.YYYY') : ''
}

export const dateWithTime = (date: Date | null | string) => {
    return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : ''
}

export const numberAsText = (amount: number | string | null, ignorePrecision?: boolean) => {
    if (amount == null) return ''

    amount = typeof amount == 'string' ? Number.parseFloat(amount) : amount

    const hasDecimalPlaces = amount % 1 != 0

    if (hasDecimalPlaces) {
        const doesEndWithZero = (amount * 100) % 10 == 0

        return doesEndWithZero
            ? c(amount, { separator: '', decimal: ',', precision: ignorePrecision ? 6 : 1, symbol: '' }).format()
            : c(amount, { separator: '', decimal: ',', precision: ignorePrecision ? 6 : 2, symbol: '' }).format()
    }

    return round(amount).toString()
}

export const currencyAsText = (amount: number | string | null) => {
    return c(amount || '', { separator: '.', decimal: ',', precision: 2, symbol: '' }).format()
}

export const getKeyForReceiptStatus = (status: ReceiptStatusHistory) => {
    let key = ''

    switch (status) {
        case ReceiptStatusHistory.Draft:
            key = 'Allaround.Status.draft'
            break
        case ReceiptStatusHistory.Sent:
        case ReceiptStatusHistory.SentEInvoice:
            key = 'Allaround.Status.invoiceSent'
            break
        case ReceiptStatusHistory.Due:
            key = 'Allaround.Status.invoicePastDue'
            break
        case ReceiptStatusHistory.Paid:
            key = 'Allaround.Status.invoicePaid'
            break
        case ReceiptStatusHistory.Cancelled:
            key = 'Allaround.Status.invoiceCancelled'
            break
        default:
            throw new Error('Not supported')
    }

    return key
}

export const getKeyForTenderStatus = (status: TenderStatusHistoryEnum) => {
    let key = ''

    switch (status) {
        case TenderStatusHistoryEnum.Draft:
            key = 'Allaround.Status.draft'
            break
        case TenderStatusHistoryEnum.Approved:
            key = 'Allaround.Status.offerAccepted'
            break
        case TenderStatusHistoryEnum.Declined:
            key = 'Allaround.Status.offerDeclined'
            break
        case TenderStatusHistoryEnum.Sent:
            key = 'Allaround.Status.offerSent'
            break
    }

    return key
}

type NormalizeNumberStringOptions = {
    allowNegative?: boolean
    allowDecimal?: boolean
    maxDecimals?: number
}

/**
 * Formats and sanitizes numeric input strings by removing invalid characters
 * and applying locale-aware decimal separator handling.
 *
 * This function accepts messy user input (with letters, multiple separators, etc.)
 * and returns a clean numeric string that can be safely parsed or displayed.
 * It intelligently handles both comma and dot as decimal separators, treating
 * the last occurrence as the actual decimal point.
 *
 * Notes: maybe tweak scenarios because pasting and direct input can behave differently,
 *        and people can have different expectations.
 *        E.g. direct input, ignore separator if there's one already there.
 *
 * @param input - The input string to format (will be converted to string if not)
 * @param options - Configuration options for formatting behavior
 * @param options.allowNegative - Whether to allow negative numbers (default: true)
 *                                If false, strips any negative signs from input
 * @param options.allowDecimal - Whether to allow decimal numbers (default: true)
 *                               If false, removes all decimal separators and treats input as integer
 * @param options.maxDecimals - Maximum number of decimal places allowed (default: 2)
 *                              Truncates decimal portion if it exceeds this limit
 *
 * @returns Clean numeric string with standardized format, or empty string if invalid
 *
 * @example
 * // Basic usage - accepts both comma and dot separators
 * normalizeNumberString("123,45", {}) // "123.45"
 * normalizeNumberString("123.45", {}) // "123.45"
 * normalizeNumberString("1.234,56", {}) // "1234.56" (last separator becomes decimal)
 *
 * // Restrict negative numbers
 * normalizeNumberString("-123,45", { allowNegative: false }) // "123.45"
 *
 * // Integer-only formatting
 * normalizeNumberString("123,45", { allowDecimal: false }) // "12345"
 *
 * // Limit decimal precision
 * normalizeNumberString("123,12345", { maxDecimals: 2 }) // "123.12"
 *
 * // Handle messy input
 * normalizeNumberString("abc123,45def", {}) // "123.45"
 * normalizeNumberString("", {}) // ""
 */
export function normalizeNumberString(input: string, options?: NormalizeNumberStringOptions): string {
    const allowNegative = options?.allowNegative ?? true
    const allowDecimal = options?.allowDecimal ?? true
    const maxDecimals = Math.max(0, Math.floor(options?.maxDecimals ?? 2))

    if (typeof input !== 'string') {
        input = String(input)
    }

    // Remove all whitespace
    let cleaned = input.trim()

    // Handle empty string
    if (!cleaned) {
        return ''
    }

    // Check for negative sign at the beginning
    let isNegative = false
    if (allowNegative && cleaned.startsWith('-')) {
        isNegative = true
        cleaned = cleaned.slice(1)
    }

    // Remove all characters except digits, and conditionally dots/commas
    if (allowDecimal) {
        cleaned = cleaned.replace(/[^0-9.,]/g, '')
    } else {
        cleaned = cleaned.replace(/[^0-9]/g, '')
    }

    let result = ''

    if (!allowDecimal) {
        // No decimals allowed, just return cleaned digits
        result = cleaned
    } else {
        // Handle decimal separator (accept both . and , but use . in output)
        // Find the last occurrence of either . or , to treat as decimal separator
        const lastDotIndex = cleaned.lastIndexOf('.')
        const lastCommaIndex = cleaned.lastIndexOf(',')

        if (lastDotIndex === -1 && lastCommaIndex === -1) {
            // No decimal separator, just digits
            result = cleaned
        } else {
            // Find which separator comes last (this will be our decimal separator)
            const decimalIndex = Math.max(lastDotIndex, lastCommaIndex)

            // Everything before the decimal separator (remove all dots and commas)
            const integerPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, '')

            // Everything after the decimal separator (remove all dots and commas)
            let decimalPart = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, '')

            // Limit decimal places to maxDecimals
            if (decimalPart.length > maxDecimals) {
                decimalPart = decimalPart.slice(0, maxDecimals)
            }

            result = `${integerPart}.${decimalPart}`
        }
    }

    // Add negative sign back if needed and allowed
    if (isNegative && allowNegative && result && result !== '0') {
        result = '-' + result
    }

    // Handle edge cases
    if (!result || result === '-') {
        return ''
    }

    return result
}
